import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CheckboxModule } from 'primeng/checkbox';
import { debounceTime, distinctUntilChanged, map, merge, startWith, Subject, takeUntil } from 'rxjs';
import { ClickOutsideDirective } from '../../../shared/directives/click-outside.directive';
import { InputCheckboxComponent } from '../../form-inputs/input-checkbox/input-checkbox.component';

interface MoreFiltersForm {
  OCCUPANCY: FormArray<FormControl<boolean | null>>;
  FINANCING: FormArray<FormControl<boolean | null>>;
  RESERVEMET: FormControl<boolean>;
  ENDINGTODAY: FormControl<boolean>;
  NOBUYERPREMIUMS: FormControl<boolean>;
  POSTAUCTIONS: FormControl<boolean>;
}

interface MoreFiltersValue {
  OCCUPANCY: (boolean | null)[];
  FINANCING: (boolean | null)[];
  RESERVEMET: boolean;
  ENDINGTODAY: boolean;
  NOBUYERPREMIUMS: boolean;
  POSTAUCTIONS: boolean;
}

const LABELS = {
  OCCUPANCY: ['Vacant', 'Occupied', 'Unknown'] as const,
  FINANCING: ['Financing Allowed', 'Cash Only'] as const,
  SECTIONS: {
    PURCHASE: 'All Purchase Options',
    OCCUPANCY: 'Occupancy',
    ADDITIONAL: 'Additional Options',
  },
  ADDITIONAL: {
    RESERVEMET: 'Reserve Met',
    ENDINGTODAY: 'Ending Today',
    POSTAUCTIONS: 'Post Auctions',
    NOBUYERPREMIUMS: 'No Buyer Premium',
  },
} as const;

@UntilDestroy()
@Component({
  selector: 'app-more',
  templateUrl: './more.component.html',
  styleUrls: ['./more.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MoreComponent),
      multi: true,
    },
  ],
  imports: [
    NgClass,
    ClickOutsideDirective,
    FontAwesomeModule,
    FormsModule,
    ReactiveFormsModule,
    CheckboxModule,
    AsyncPipe,
    InputCheckboxComponent,
  ],
})
export class MoreComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();
  private readonly countUpdate$ = new Subject<void>();

  readonly isMobile = input<boolean>(false);
  readonly onFormChange = output<MoreFiltersValue>();

  isOpen = false;
  readonly labels = LABELS;

  private onChange?: (value: MoreFiltersValue) => void;
  private onTouched = () => {};

  readonly occupancyArray = this.createBooleanFormArray(3);
  readonly financingArray = this.createBooleanFormArray(2);

  readonly moreFormGroup = this.fb.group<MoreFiltersForm>({
    OCCUPANCY: this.occupancyArray,
    FINANCING: this.financingArray,
    RESERVEMET: this.fb.control<boolean>(false, { nonNullable: true }),
    ENDINGTODAY: this.fb.control<boolean>(false, { nonNullable: true }),
    NOBUYERPREMIUMS: this.fb.control<boolean>(false, { nonNullable: true }),
    POSTAUCTIONS: this.fb.control<boolean>(false, { nonNullable: true }),
  });

  readonly numberOfSelected$ = merge(
    this.moreFormGroup.valueChanges,
    this.countUpdate$
  ).pipe(
    startWith(null),
    debounceTime(50),
    map(() => this.calculateSelectedCount(this.moreFormGroup.value as MoreFiltersValue)),
    distinctUntilChanged(),
  );

  ngOnInit(): void {
    this.setupFinancingExclusion();
    this.setupFormValueSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: Partial<MoreFiltersValue> | null): void {
    if (value && Object.keys(value).length > 0) {
      this.moreFormGroup.patchValue(value, { emitEvent: false });
      this.countUpdate$.next();
    }
  }

  registerOnChange(fn: (value: MoreFiltersValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.moreFormGroup.disable() : this.moreFormGroup.enable();
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.onTouched();
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.onTouched();
  }

  private createBooleanFormArray(size: number): FormArray<FormControl<boolean | null>> {
    return this.fb.array(
      Array(size)
        .fill(null)
        .map(() => this.fb.control<boolean | null>(null))
    );
  }

  private setupFinancingExclusion(): void {
    const setupExclusion = (triggerIndex: number, targetIndex: number) => {
      this.financingArray
        .at(triggerIndex)
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe(value => {
          if (value === true) {
            this.financingArray.at(targetIndex).setValue(false);
          }
        });
    };

    setupExclusion(0, 1);
    setupExclusion(1, 0);
  }

  private setupFormValueSubscription(): void {
    this.moreFormGroup.valueChanges
      .pipe(
        debounceTime(50),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        untilDestroyed(this),
      )
      .subscribe(value => {
        const typedValue = value as MoreFiltersValue;
        this.onChange?.(typedValue);
        this.onFormChange.emit(typedValue);
      });
  }

  resetForm(): void {
    this.moreFormGroup.reset({
      OCCUPANCY: [null, null, null],
      FINANCING: [null, null],
      RESERVEMET: false,
      ENDINGTODAY: false,
      NOBUYERPREMIUMS: false,
      POSTAUCTIONS: false,
    });
  }

  private calculateSelectedCount(value: MoreFiltersValue): number {
    const booleanFields = ['ENDINGTODAY', 'NOBUYERPREMIUMS', 'POSTAUCTIONS', 'RESERVEMET'] as const;
    const arrayFields = ['FINANCING', 'OCCUPANCY'] as const;

    const booleanCount = booleanFields.reduce((sum, field) => sum + Number(value[field]), 0);

    const arrayCount = arrayFields.reduce((sum, field) => {
      return sum + (value[field]?.filter(Boolean).length || 0);
    }, 0);

    return booleanCount + arrayCount;
  }

  get occupancyControls(): FormControl[] {
    return this.occupancyArray.controls;
  }

  get financingControls(): FormControl[] {
    return this.financingArray.controls;
  }
}